import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Observable, map } from 'rxjs';
import { Authorities } from '@shared/constants/authorities';
import { Icons } from '@shared/constants/icons';
import { UserState } from '@shared/states/user.state';

/** `null` means there is nothing to warn about, so the banner collapses. */
export type PaymentOverdueBannerVariant = 'warning' | 'lastDay' | 'suspended';

export interface PaymentOverdueBannerState {
  daysLeft: number;
  variant: PaymentOverdueBannerVariant;
}

/**
 * Warns about an overdue subscription payment above the navbar, in three variants: more than a day
 * left (warning), the last day before suspension (danger) and already suspended (danger) — the last
 * one only for the users PaymentSuspendedCanActivateChildGuard parks on the billing page, since
 * everyone else lands on /access-suspended, which states the same thing in full.
 */
@Component({
  selector: 'app-payment-overdue-banner',
  templateUrl: './payment-overdue-banner.component.html',
  styleUrls: ['./payment-overdue-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PaymentOverdueBannerComponent {
  public readonly attentionIcon = Icons.StatusAttentionOutline_16;

  /** Clients never see their company's billing state. */
  public readonly canSeeBanner: boolean = !this.userState.isClient;

  /** Paying requires the authority to change the subscription, not just to look at it. */
  public readonly canPay: boolean = this.userState.hasAuthorities([Authorities.BillingModify]);

  public readonly state$: Observable<PaymentOverdueBannerState> =
    this.userState.paymentDaysLeft$.pipe(
      map((daysLeft) => ({
        daysLeft,
        variant: this.resolveVariant(daysLeft),
      })),
    );

  constructor(private userState: UserState) {}

  private resolveVariant(daysLeft: number): PaymentOverdueBannerVariant {
    // daysLeft === PaymentGracePeriodDays means nothing is overdue at all.
    if (!this.canSeeBanner || daysLeft >= this.userState.PaymentGracePeriodDays) {
      return null;
    }

    if (daysLeft > 0) {
      return 'warning';
    }

    if (daysLeft === 0) {
      return 'lastDay';
    }

    return this.userState.canResolvePaymentOverdue ? 'suspended' : null;
  }
}
